import { Visibility, VisibilityOff } from '@mui/icons-material'
import {
  Box,
  Button,
  DialogActions,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Modal,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type React from 'react'
import { useContext, useEffect, useRef, useState } from 'react'
import { loginIdSchema, strictPasswordSchema } from '../schema/loginSchema'
import { salesCompanySchema } from '../schema/organizationSchema'
import { LanguageContext } from '../util/LanguageContext'

type ModalUserAddDialogProps = {
  open: boolean
  title: string
  onClose: () => void
  onSave: (args: {
    loginId: string
    password: string
    rhq?: string
    salesCompany?: string
    role: number
  }) => void
}

export const ModalUserAddDialog: React.FC<ModalUserAddDialogProps> = props => {
  const { open, onClose, onSave } = props
  const [isOpen, setIsOpen] = useState(open)
  const [salesCompany, setSalesCompany] = useState('')
  const [loginId, setLoginId] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState(3) // デフォルトは一般を選択状態にする
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [errorSalesCompany, setErrorSalesCompany] = useState('')
  const [errorLoginId, setErrorLoginId] = useState('')
  const [errorPassword, setErrorPassword] = useState('')
  // 文字列変換用
  const translation = useContext(LanguageContext)

  const validateSalesCompany = async (value: string) => {
    try {
      await salesCompanySchema.validate(value)
      setErrorSalesCompany('')
      if (salesCompany !== '' && loginId !== '' && password !== '' && errorPassword === '')
        setIsButtonDisabled(false)
    } catch (err: any) {
      setErrorSalesCompany(err.message)
      setIsButtonDisabled(true)
    }
  }

  const validateLoginId = async (value: string) => {
    try {
      await loginIdSchema.validate(value)
      setErrorLoginId('')
      if (salesCompany !== '' && loginId !== '' && password !== '' && errorPassword === '')
        setIsButtonDisabled(false)
    } catch (err: any) {
      setErrorLoginId(err.message)
      setIsButtonDisabled(true)
    }
  }

  const validatePassword = async (value: string) => {
    try {
      await strictPasswordSchema.validate(value)
      setErrorPassword('')
      if (salesCompany !== '' && loginId !== '' && password !== '' && errorPassword === '')
        setIsButtonDisabled(false)
    } catch (err: any) {
      setErrorPassword(err.message)
      setIsButtonDisabled(true)
    }
  }

  const handleSalesCompanyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSalesCompany(event.target.value)
    validateSalesCompany(event.target.value)
  }

  const handleLoginidChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoginId(event.target.value)
    validateLoginId(event.target.value)
    // setIsButtonDisabled(event.target.value === "" || password === "");
  }

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
    validatePassword(event.target.value)
    // setIsButtonDisabled(event.target.value === "" || loginId === "");
  }
  const handleRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRole(Number(event.target.value))
    setIsButtonDisabled(event.target.value === '' || loginId === '')
  }

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev)
  }

  const handleSave = () => {
    // console.log(`salesCompany:${salesCompany} loginId:${loginId} password:${password} role:${role}`);
    onSave({ salesCompany, loginId, password, role })
    onClose()
    setIsOpen(false)
  }
  const handleClose = (event: React.MouseEvent<HTMLInputElement>, reason: 'backdropClick') => {
    if (reason === 'backdropClick') return
    onClose()
    setIsOpen(false)
  }
  useEffect(() => {
    setIsButtonDisabled(loginId === '' || password === '')
  }, [loginId, password, role])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          height: '700px',
          width: '787px',
          bgcolor: 'background.paper',
          borderRadius: '10px',
          boxShadow: '0px 2px 2px #00000014',
        }}
      >
        <Stack direction="column" justifyContent="center" alignItems="center">
          {/* ダイアログタイトル: 新規追加 */}
          <Typography
            sx={{
              mt: '48px',
              font: 'normal normal bold 32px/48px BIZ UDGothic',
              textAlign: 'center',
            }}
          >
            {translation['button_add_new']}
          </Typography>

          {/* Sales company */}
          <Typography
            sx={{
              left: '190px',
              width: '402px',
              mt: '50px',
              color: '#282828',
              font: 'normal normal normal 21px/32px Noto Sans JP',
              textAlign: 'left',
            }}
          >
            {translation['label_SalesCompany_info']}
          </Typography>
          <TextField
            value={salesCompany}
            error={!!errorSalesCompany}
            helperText={errorSalesCompany ? translation[errorSalesCompany] : ' '}
            onChange={handleSalesCompanyChange}
            InputProps={{ sx: { background: '#F4F4F4' } }}
            sx={{
              width: '402px',
              borderRadius: '10px',
              '& .MuiFormHelperText-root': {
                whiteSpace: 'nowrap',
              },
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: errorSalesCompany ? 'red' : '#282828',
                },
              },
            }}
          />

          {/* ID */}
          <Typography
            sx={{
              left: '190px',
              width: '402px',
              color: '#282828',
              font: 'normal normal normal 21px/32px Noto Sans JP',
              textAlign: 'left',
            }}
          >
            {translation['label_id']}
          </Typography>
          <TextField
            value={loginId}
            error={!!errorLoginId}
            helperText={errorLoginId ? translation[errorLoginId] : ' '}
            onChange={handleLoginidChange}
            InputProps={{ sx: { background: '#F4F4F4' } }}
            sx={{
              width: '402px',
              borderRadius: '10px',
              '& .MuiFormHelperText-root': {
                whiteSpace: 'nowrap',
              },
              '& .MuiOutlinedInput-root': {
                // "& fieldset": {
                //   borderColor: "white",
                // },
                // "&:hover fieldset": {
                //   borderColor: "white",
                // },
                '&.Mui-focused fieldset': {
                  borderColor: errorLoginId ? 'red' : '#282828',
                },
              },
            }}
          />

          {/* パスワード */}
          <Typography
            sx={{
              left: '190px',
              width: '402px',
              color: '#282828',
              font: 'normal normal normal 21px/32px Noto Sans JP',
              textAlign: 'left',
            }}
          >
            {translation['label_password']}
          </Typography>
          <TextField
            type={showPassword ? 'text' : 'password'}
            value={password}
            error={!!errorPassword}
            helperText={errorPassword ? translation[errorPassword] : ' '}
            onChange={handlePasswordChange}
            FormHelperTextProps={{
              style: { whiteSpace: 'normal' }, // ここで改行を許可します
            }}
            InputProps={{
              endAdornment: password ? (
                <InputAdornment position="end">
                  <IconButton onClick={handleTogglePasswordVisibility}>
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: {
                background: '#F4F4F4',
              },
            }}
            sx={{
              width: '402px',
              height: '100px',
              borderRadius: '10px',
              'input::-ms-reveal': {
                display: 'none' /* -ms-revealアイコンを非表示にする */,
              },
              '& .MuiFormHelperText-root': {
                whiteSpace: 'nowrap',
              },
              '& .MuiOutlinedInput-root': {
                // "& fieldset": {
                //   borderColor: "white",
                // },
                // "&:hover fieldset": {
                //   borderColor: "white",
                // },
                '&.Mui-focused fieldset': {
                  borderColor: errorPassword ? 'red' : '#282828',
                },
              },
            }}
            // variant="outlined"
          />

          {/* 権限 */}
          <Typography
            sx={{
              left: '190px',
              width: '402px',
              color: '#282828',
              font: 'normal normal normal 21px/32px Noto Sans JP',
              textAlign: 'left',
            }}
          >
            {translation['label_authority']}
          </Typography>
          <RadioGroup
            row
            value={role}
            onChange={handleRoleChange}
            sx={{
              width: '300px',
              left: '200px',
              display: 'flex',
              justifyContent: 'space-between', // 両端揃え
            }}
          >
            {/* 一般 - 3:キッティング業者(RHQ Member, Kitting operator) */}
            <FormControlLabel
              value="3"
              control={
                <Radio
                  sx={{
                    color: '#282828',
                    '&.Mui-checked': {
                      color: '#282828',
                    },
                  }}
                />
              }
              label={translation['label_authority_general']}
            />
            {/* 管理者 - 2:RHQ Administrator */}
            <FormControlLabel
              value="2"
              control={
                <Radio
                  sx={{
                    color: '#282828',
                    '&.Mui-checked': {
                      color: '#282828',
                    },
                  }}
                />
              }
              label={translation['label_authority_admin']}
            />
          </RadioGroup>

          <DialogActions>
            <Button
              onClick={onClose}
              sx={{
                height: '49px',
                width: '181px',
                font: 'normal normal bold 21px/29px Noto Sans JP',
                color: '#282828',
                background: '#FFFFFF 0% 0% no-repeat padding-box',
                border: '2px solid #282828',
                borderRadius: '10px',
                mt: '5px',
                mr: '30px',
                '&:hover': {
                  background: '#FFFFFF',
                  opacity: 0.8,
                },
              }}
            >
              {translation['button_cancel']}
            </Button>
            <Button
              type="submit"
              disabled={isButtonDisabled}
              onClick={handleSave}
              sx={{
                height: '49px',
                width: '181px',
                font: 'normal normal bold 21px/29px Noto Sans JP',
                color: isButtonDisabled ? '#CACACA' : '#FFFFFF',
                background: isButtonDisabled ? '#F4F4F4 0% 0% no-repeat padding-box' : '#282828',
                borderRadius: '10px',
                mt: '5px',
                '&:hover': {
                  backgroundColor: '#282828',
                  opacity: 0.8,
                  boxShadow: 0,
                },
              }}
            >
              {translation['button_add']}
            </Button>
          </DialogActions>
        </Stack>
      </Box>
    </Modal>
  )
}
